import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';

import { upsertSupplierShippingPriceApi } from '../suppliers.api';

const parseDisplayCost = (displayValue) => {
  if (displayValue == null || displayValue === '' || displayValue === '—') {
    return '';
  }
  const cleaned = String(displayValue).replace(/[^0-9.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n.toFixed(2) : '';
};

const isSetSku = (sku) => typeof sku === 'string' && sku.trim().includes(' ');

const UpdateSupplierShippingModal = ({
  isOpen,
  onClose,
  supplierId,
  supplierName,
  sku,
  initialCost,
  inventoryIncludesShipping,
  onSaved,
  textColor,
  secondaryTextColor,
  borderColor,
}) => {
  const dispatch = useDispatch();
  const { upsertShippingPriceStatus } = useSelector((state) => state.suppliers);

  const inputBg = useColorModeValue('white', 'gray.700');
  const inputTextColor = useColorModeValue('gray.800', 'white');
  const modalBg = useColorModeValue('white', 'gray.800');
  const defaultBorderColor = useColorModeValue('gray.200', 'gray.600');
  const resolvedBorderColor = borderColor || defaultBorderColor;

  const [cost, setCost] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCost(parseDisplayCost(initialCost));
    }
  }, [isOpen, initialCost]);

  const cannotEdit = inventoryIncludesShipping !== false || !supplierId;
  const setSku = isSetSku(sku);

  const handleSave = async () => {
    const parsed = Number(cost);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return;
    }

    const result = await dispatch(
      upsertSupplierShippingPriceApi({
        id: supplierId,
        sku: (sku || '').trim(),
        cost: parsed,
      }),
    );

    if (upsertSupplierShippingPriceApi.fulfilled.match(result) && result.payload?.success) {
      onSaved?.(result.payload?.data);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="lg" maxW="420px" bg={modalBg}>
        <ModalHeader color={textColor} fontSize="lg">
          Update shipping
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={2}>
          <VStack align="stretch" spacing={4}>
            {supplierName && (
              <Text fontSize="sm" color={secondaryTextColor}>
                Supplier: <Text as="span" color={textColor} fontWeight="medium">{supplierName}</Text>
              </Text>
            )}
            {cannotEdit ? (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {inventoryIncludesShipping !== false
                  ? 'This supplier includes shipping in inventory. External shipping cannot be edited.'
                  : 'Supplier is missing. Cannot update shipping.'}
              </Alert>
            ) : (
              <>
                <FormControl>
                  <FormLabel fontSize="sm" color={secondaryTextColor}>
                    SKU
                  </FormLabel>
                  <Input
                    value={sku || ''}
                    isReadOnly
                    size="sm"
                    color={inputTextColor}
                    bg={inputBg}
                    borderColor={resolvedBorderColor}
                    _readOnly={{ opacity: 1, cursor: 'default' }}
                  />
                </FormControl>
                {setSku && (
                  <Text fontSize="xs" color={secondaryTextColor}>
                    Set SKU detected — the cost will be split evenly across each part when saved.
                  </Text>
                )}
                <FormControl>
                  <FormLabel fontSize="sm" color={secondaryTextColor}>
                    Shipping cost ($)
                  </FormLabel>
                  <NumberInput
                    min={0}
                    precision={2}
                    value={cost}
                    onChange={(_, v) => setCost(v)}
                    size="sm"
                  >
                    <NumberInputField
                      color={inputTextColor}
                      bg={inputBg}
                      borderColor={resolvedBorderColor}
                    />
                  </NumberInput>
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={handleSave}
            isLoading={upsertShippingPriceStatus === 'pending'}
            isDisabled={cannotEdit || cost === ''}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpdateSupplierShippingModal;
